import { useMemo } from "react";
import { ModelsTheme } from "@src/api/v1/data-contracts";

export interface QuestionListItem {
  score: number;
  createdAt: string;
  key?: string | null;
}

export interface ScoredQuestionList {
  createdAt: string;
  key?: string | null;
  score: number;
}

interface UseScoredQuestionListsResult {
  scoredQuestionLists: ScoredQuestionList[];
  themesWithQuestions: ModelsTheme[];
}

export const useScoredQuestionLists = (
  medicalThemeData: ModelsTheme[] | undefined,
): UseScoredQuestionListsResult => {
  return useMemo(() => {
    if (!medicalThemeData || medicalThemeData.length === 0) {
      return {
        scoredQuestionLists: [],
        themesWithQuestions: [],
      };
    }

    const flattenedQuestionLists: QuestionListItem[] = medicalThemeData.flatMap(
      (theme) =>
        (theme.questionLists || []).map((ql) => ({
          score: ql.score ?? 0,
          createdAt: ql.createdAt ?? "",
          key: ql.key,
        })),
    );

    if (flattenedQuestionLists.length === 0) {
      return {
        scoredQuestionLists: [],
        themesWithQuestions: [],
      };
    }
    const grouped = Object.groupBy(
      flattenedQuestionLists,
      (item) => item.createdAt,
    );
    const groupedQuestionLists = Object.values(grouped) as QuestionListItem[][];

    const scoredQuestionLists: ScoredQuestionList[] = groupedQuestionLists.map(
      (questionLists) => ({
        createdAt: questionLists[0].createdAt,
        key: questionLists[0].key,
        score: questionLists.reduce((sum, element) => sum + element.score, 0),
      }),
    );

    const themesWithQuestions = medicalThemeData.filter(
      (theme) => theme.questionLists && theme.questionLists.length > 0,
    );

    return {
      scoredQuestionLists,
      themesWithQuestions,
    };
  }, [medicalThemeData]);
};
